import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import useAuth from '../../hooks/useAuth';

import { formatDate } from '../../utils/Format'

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('fr');

const groupNotificationsByDate = (notifications) => {

    const groups = {};

    notifications.forEach((notif) => {
        const date = dayjs(notif.Crea_Date);
        const today = dayjs();
        const yesterday = dayjs().subtract(1, 'day');

        let label;
        if (date.isSame(today, 'day')) {
            label = "Aujourd'hui";
        } else if (date.isSame(yesterday, 'day')) {
            label = "Hier";
        } else {
            const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
            label = `Le ${capitalize(date.format('dddd'))} ${date.format('D')} ${capitalize(date.format('MMMM'))} ${date.format('YYYY')}`;
        }

        if (!groups[label]) {
            groups[label] = {
                label,
                date: date.startOf('day'),
                notifs: []
            };
        }

        groups[label].notifs.push(notif);
    });

    return Object.values(groups)
        .sort((a, b) => b.date.valueOf() - a.date.valueOf())
        .map(({ label, notifs }) => {
            const lues = notifs
                .filter(n => n.Consulte_Date)
                .sort((a, b) =>
                    dayjs(`${b.Crea_Date}T${parseHeure(b.Crea_Heure)}`).valueOf() -
                    dayjs(`${a.Crea_Date}T${parseHeure(a.Crea_Heure)}`).valueOf()
                );

            const nonLues = notifs
                .filter(n => !n.Consulte_Date)
                .sort((a, b) =>
                    dayjs(`${b.Crea_Date}T${parseHeure(b.Crea_Heure)}`).valueOf() -
                    dayjs(`${a.Crea_Date}T${parseHeure(a.Crea_Heure)}`).valueOf()
                );

            return [label, [...nonLues, ...lues]];
        });
};


const parseHeure = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds)) return "00:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}`;
};

const getPreciseTimeAgo = (dateStr, heureExcel) => {
    const totalSeconds = parseInt(heureExcel, 10);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const notifTime = dayjs(dateStr)
        .hour(hours)
        .minute(minutes)
        .second(seconds);

    const now = dayjs();

    const diffMinutes = now.diff(notifTime, 'minute');
    if (diffMinutes > 1440 || diffMinutes < 0) return null;

    const h = Math.floor(diffMinutes / 60);
    const m = diffMinutes % 60;

    if (h === 0) return `il y a ${m} min`;
    if (m === 0) return `il y a ${h} heure${h > 1 ? 's' : ''}`;
    return `il y a ${h} heure${h > 1 ? 's' : ''} et ${m} min`;
};

const getNotificationIcon = (comment) => {
    const normalized = comment
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const iconMap = [
        {
            keywords: ["creation", "cree", "create"],
            icon: "fa-plus",
            colorClass: "text-teal-400"
        },
        {
            keywords: ["modification", "modifie", "modif"],
            icon: "fa-pen-to-square",
            colorClass: "text-yellow-400"
        },
        {
            keywords: ["suppression", "supprime", "delete"],
            icon: "fa-trash",
            colorClass: "text-red-400"
        },
        {
            keywords: ["validation", "valide"],
            icon: "fa-check",
            colorClass: "text-blue-400"
        }
    ];


    const match = iconMap.find(({ keywords }) =>
        keywords.some(keyword => normalized.includes(keyword))
    );

    return match ? (
        <i className={`fa-solid ${match.icon} ${match.colorClass}`}></i>
    ) : (
        <i className="text-gray-400 fa-solid fa-bell"></i>
    );

};

const Notifications = ({ notifications, refreshUserNotifications }) => {
    const navigate = useNavigate();

    const { editNotification } = useAuth();

    const [showAllNotifications, setShowAllNotifications] = useState(true);
    const [localNotifications, setLocalNotifications] = useState(notifications);

    const [expandedNotificationId, setExpandedNotificationId] = useState(null);

    useEffect(() => {
        setLocalNotifications(notifications);
    }, [notifications]);

    const toggleExpand = (id) => {
        setExpandedNotificationId(prev => prev === id ? null : id);
    };


    const handleToggleRead = (notif, isUnread) => {
        const now = dayjs();
        const updatedNotif = {
            ...notif,
            Consulte_Date: isUnread ? now.format('YYYY-MM-DD') : null,
            Consulte_Heure: isUnread
                ? now.hour() * 3600 + now.minute() * 60 + now.second()
                : null,
        };

        editNotification(updatedNotif)
            .then(() => {
                if (typeof refreshUserNotifications === 'function') {
                    refreshUserNotifications();
                }
            });

        setLocalNotifications((prev) =>
            prev.map((n) =>
                n.Crea_Date === notif.Crea_Date &&
                    n.Crea_Heure === notif.Crea_Heure &&
                    n.Nom_Utilisateur === notif.Nom_Utilisateur
                    ? updatedNotif
                    : n
            )
        );
    };

    const grouped = Array.isArray(localNotifications) && localNotifications.length > 0
        ? groupNotificationsByDate(showAllNotifications ? localNotifications : localNotifications.filter(n => n.Consulte_Date == null))
        : [];

    return (
        <>
            <div className="flex justify-between items-center">
                <p
                    tabIndex="0"
                    className="focus:outline-none font-semibold text-gray-200 text-2xl leading-6"
                >
                    Notifications
                </p>
                <label class="flex gap-2 min-w-36 mcui-checkbox">
                    <input
                        type="checkbox"
                        checked={showAllNotifications}
                        onChange={() => setShowAllNotifications(!showAllNotifications)}
                    />
                    <div>
                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                        </svg>
                    </div>
                    <span className="text-gray-300 text-lg">Afficher les Notifications non lues</span>
                </label>
            </div>
            {grouped.map(([label, notifs]) => (
                <div key={label}>
                    <h2
                        tabIndex="0"
                        className="pt-8 pb-2 border-gray-300 border-b focus:outline-none text-white text-base leading-normal"
                    >
                        {label}
                        {notifs.filter(notif => !notif.Consulte_Date).length > 0 &&
                            (<>
                                <span className="bg-orange-600 ml-3 px-1.5 rounded-xl h-full font-base text-gray-500 text-white text-xs">{notifs.length > 0 ? `${notifs.filter(notif => notif.Consulte_Date == null).length} non lues` : ''}</span>
                                <button className="group ml-3 px-2 rounded text-orange-700 hover:text-orange-600 text-xs transition-all duration-300 ease-in-out"

                                    onClick={(e) => {
                                        e.stopPropagation();

                                        notifs.forEach((notif) => {
                                            if (notif.Consulte_Date) return;

                                            handleToggleRead(notif, true);
                                        });
                                    }}

                                >
                                    Tout marquer comme lu
                                </button>
                            </>)}
                    </h2>
                    <label ></label>
                    {notifs
                        .map((notif, idx) => {
                            const uniqueId = `${notif.Crea_Date}-${notif.Crea_Heure}-${idx}`;
                            const timeText = getPreciseTimeAgo(notif.Crea_Date, notif.Crea_Heure);
                            const isUnread = notif.Consulte_Date == null;

                            return (
                                <div
                                    key={idx}
                                    className={`flex flex-col gap-1 mt-4  w-full transition-all duration-300 ease-in-out cursor-pointer`}
                                    onClick={() => toggleExpand(uniqueId)}
                                >
                                    <div className={`flex justify-between gap-2 p-3 rounded-md ${isUnread ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 hover:bg-gray-700'} transition-all duration-300`}>
                                        <div className="flex flex-shrink-0 justify-center items-center">
                                            <div
                                                tabIndex="0"
                                                aria-label="notif icon"
                                                role="img"
                                                className="flex justify-center items-center mr-2 border border-white rounded-full w-8 h-8"
                                            >
                                                {getNotificationIcon(notif.Commentaire)}
                                            </div>
                                        </div>

                                        <div className={`flex-grow`}>
                                            <p className="focus:outline-none text-white text-sm leading-snug">
                                                <span className="font-semibold text-orange-500">{notif.Nom_Utilisateur.charAt(0).toUpperCase() + notif.Nom_Utilisateur.slice(1)}</span>{" "}
                                                {notif.Commentaire}
                                                {notif.Info_Origine && (
                                                    <p className="flex gap-2 font-semibold text-orange-400 text-xs">
                                                        {(() => {
                                                            const full = notif.Info_Origine.Nom || notif.Info_Origine.Libelle || notif.Info_Origine.Nom_Du_Document || JSON.stringify(notif.Info_Origine);
                                                            const extMatch = full.match(/\.[a-z0-9]{2,5}$/i);
                                                            const ext = extMatch ? extMatch[0] : '';
                                                            const withoutExt = ext ? full.slice(0, -ext.length) : full;
                                                            if (full.length > 30) {
                                                                const base = withoutExt.slice(0, 30 - ext.length);
                                                                return base + '…' + ext;
                                                            }
                                                            return full;
                                                        })()} {notif.Info_Origine?.Suppression &&
                                                            (<span class="text-red-500">( <i class="mr-1 fa-solid fa-xmark"></i>Document Supprimé par {notif.Info_Origine?.Complements?.User.charAt(0).toUpperCase() + notif.Info_Origine?.Complements?.User.slice(1)} )</span>)
                                                        }
                                                    </p>
                                                )}


                                            </p>
                                            <p className="pt-1 text-gray-200 text-xs leading-3">
                                                {timeText ? `${timeText}` : formatDate(notif.Crea_Date)}
                                            </p>
                                        </div>

                                        <div className="flex flex-shrink-0 justify-center items-center">
                                            <button className="group bg-orange-700 px-2 py-1 rounded transition-all duration-300 ease-in-out"

                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    handleToggleRead(notif, isUnread);
                                                }}

                                            >
                                                <i
                                                    className={`fa-solid ${isUnread
                                                        ? 'fa-envelope text-orange-100 group-hover:animate-none animate-[wiggle_1s_ease-in-out_infinite]'
                                                        : 'fa-envelope-open text-orange-300 group-hover:text-orange-100'
                                                        } transition-all duration-300 ease-in-out`}
                                                ></i>
                                            </button>
                                        </div>
                                    </div>


                                    <div
                                        className={`
                                                        transition-all duration-300 ease-in-out overflow-hidden
                                                        ${expandedNotificationId === uniqueId ? 'max-h-48 mt-1' : 'max-h-0'}
                                                    `}
                                    >
                                        <div className="flex justify-around gap-2 *:py-0.5 *:border-orange-600 *:rounded *:w-1/4 *:text-orange-600 *:text-center">
                                           {/* <a className="group relative border-2 rounded-md overflow-hidden font-semibold transition-all duration-300"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <span className="top-0 left-0 z-10 absolute bg-orange-600 w-full h-full transition-transform -translate-x-full group-hover:translate-x-0 duration-300 ease-in-out transform"></span>
                                                <span className="z-20 relative group-hover:text-white transition-all duration-300"></span>
                                            </a>
                                            <a className="group relative border-2 rounded-md overflow-hidden font-semibold transition-all duration-300"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <span className="top-0 left-0 z-10 absolute bg-orange-600 w-full h-full transition-transform -translate-x-full group-hover:translate-x-0 duration-300 ease-in-out transform"></span>
                                                <span className="z-20 relative group-hover:text-white transition-all duration-300"></span>
                                            </a>*/}
                                            {!notif.Info_Origine?.Suppression && notif.Info_Origine?.UUID_Externe_ && notif.NomTableExterne && (
                                                <button
                                                    type="button"
                                                    className="group relative border-2 rounded-md overflow-hidden font-semibold transition-all duration-300"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleRead(notif, true);

                                                        navigate(
                                                            `/${notif.NomTableExterne}/${notif.Info_Origine.UUID_Externe_}`,
                                                            {
                                                                state: {
                                                                    openDocumentFromNotif: {
                                                                        uuidDoc: notif.Info_Origine.UUID_,
                                                                        infoOrigine: notif.Info_Origine,
                                                                    },
                                                                },
                                                            }
                                                        );
                                                    }}
                                                >
                                                    <span className="top-0 left-0 z-10 absolute bg-orange-600 w-full h-full transition-transform -translate-x-full group-hover:translate-x-0 duration-300 ease-in-out transform"></span>
                                                    <span className="z-20 relative group-hover:text-white transition-all duration-300">
                                                        Voir le fichier
                                                    </span>
                                                </button>
                                            )}

                                           {/* <a className="group relative border-2 rounded-md overflow-hidden font-semibold transition-all duration-300"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <span className="top-0 left-0 z-10 absolute bg-orange-600 w-full h-full transition-transform -translate-x-full group-hover:translate-x-0 duration-300 ease-in-out transform"></span>
                                                <span className="z-20 relative group-hover:text-white transition-all duration-300"></span>
                                            </a>*/}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div >
            ))}
            <div class="flex items-center justiyf-between">
                <hr class="w-full"></hr>
                <p tabindex="0" class="flex flex-shrink-0 px-3 py-16 focus:outline-none text-gray-200 text-sm leading-normal">C'est tout pour l'instant :)</p>
                <hr class="w-full"></hr>
            </div>
        </>
    );
};

export default Notifications;
